import { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function makeSessionId() {
  const existing = localStorage.getItem("space_architect_session");
  if (existing) return existing;
  const fresh = "arch-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
  localStorage.setItem("space_architect_session", fresh);
  return fresh;
}

const SESSION_ID = makeSessionId();

const OPENING_MESSAGE = {
  role: "assistant",
  content:
    "You have arrived here because a signal in one of your five pillars is distorted. That distortion has been costing you clarity, income, or peace — often all three. My work is to name the exact architecture beneath what you are feeling.\n\nTell me: what brought you to this crossroad today?",
};

export default function ArchitectChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const stored = localStorage.getItem("space_architect_msgs");
    if (stored) {
      try { return JSON.parse(stored); } catch { /* ignore */ }
    }
    return [OPENING_MESSAGE];
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [visitor, setVisitor] = useState(() => {
    try { return JSON.parse(localStorage.getItem("space_architect_visitor") || "{}"); }
    catch { return {}; }
  });
  const [showEmailForm, setShowEmailForm] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("space_architect_msgs", JSON.stringify(messages));
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    localStorage.setItem("space_architect_visitor", JSON.stringify(visitor));
  }, [visitor]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/architect/chat`, {
        session_id: SESSION_ID,
        message: text,
        visitor_name: visitor.name || null,
        visitor_email: visitor.email || null,
      }, { timeout: 60000 });
      const reply = res.data.reply || "";
      const actions = res.data.actions || [];
      setMessages((m) => [...m, { role: "assistant", content: reply, actions }]);
      // Handle actions
      for (const a of actions) {
        if (a.type === "show_bundle") {
          setTimeout(() => {
            const el = document.getElementById("bundle-section");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 400);
        }
        if (a.type === "show_quiz") {
          setTimeout(() => {
            const el = document.getElementById("quiz");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 400);
        }
        if (a.type === "capture_email" && a.email) {
          setVisitor({ name: a.name || visitor.name, email: a.email });
        }
      }
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "The signal broke momentarily. Speak again — the architecture holds.",
      }]);
    }
    setBusy(false);
  }

  function onKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  function reset() {
    if (!window.confirm("Reset this conversation? Your architect session will restart.")) return;
    localStorage.removeItem("space_architect_session");
    localStorage.removeItem("space_architect_msgs");
    localStorage.removeItem("space_architect_visitor");
    window.location.reload();
  }

  function submitVisitorInfo(e) {
    e.preventDefault();
    setShowEmailForm(false);
    if (visitor.email && visitor.name) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Received, ${visitor.name.split(" ")[0]}. Your architecture is now anchored to your name. The next turn moves us deeper.`,
      }]);
    }
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="architect-bubble"
          data-testid="architect-bubble"
          aria-label="Open S.P.A.C.E. Architect chat"
        >
          <span className="architect-bubble-ring" aria-hidden="true" />
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span className="architect-bubble-label">Architect</span>
        </button>
      )}

      {open && (
        <div className="architect-panel" data-testid="architect-panel" role="dialog" aria-label="S.P.A.C.E. Architect">
          <header className="architect-header">
            <div>
              <p className="architect-eyebrow">S.P.A.C.E. Academy</p>
              <h3 className="architect-title">The Architect</h3>
            </div>
            <div className="architect-header-actions">
              <button type="button" className="architect-icon-btn" onClick={reset} title="Reset conversation" data-testid="architect-reset">↺</button>
              <button type="button" className="architect-icon-btn" onClick={() => setOpen(false)} title="Close" data-testid="architect-close">×</button>
            </div>
          </header>

          <div className="architect-messages" ref={scrollRef} data-testid="architect-messages">
            {messages.map((m, i) => (
              <div key={i} className={`architect-msg architect-msg-${m.role}`}>
                <div className="architect-msg-body">
                  {m.content.split("\n").map((line, j) => (
                    <p key={j} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
                  ))}
                </div>
              </div>
            ))}
            {busy && (
              <div className="architect-msg architect-msg-assistant">
                <div className="architect-msg-body architect-typing">
                  <span /> <span /> <span />
                </div>
              </div>
            )}
          </div>

          {showEmailForm ? (
            <form className="architect-visitor-form" onSubmit={submitVisitorInfo} data-testid="architect-visitor-form">
              <input type="text" placeholder="Your name" value={visitor.name || ""}
                onChange={(e) => setVisitor({ ...visitor, name: e.target.value })} required data-testid="architect-visitor-name" />
              <input type="email" placeholder="Your email" value={visitor.email || ""}
                onChange={(e) => setVisitor({ ...visitor, email: e.target.value })} required data-testid="architect-visitor-email" />
              <button type="submit" data-testid="architect-visitor-submit">Anchor</button>
            </form>
          ) : !visitor.email && (
            <button type="button" className="architect-visitor-toggle" onClick={() => setShowEmailForm(true)} data-testid="architect-anchor-toggle">
              + Anchor Your Identity (name &amp; email)
            </button>
          )}

          <form className="architect-input-row" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea
              className="architect-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={busy ? "The Architect is thinking…" : "Speak. The architecture is listening."}
              rows={2}
              disabled={busy}
              data-testid="architect-input"
            />
            <button type="submit" className="architect-send" disabled={busy || !input.trim()} data-testid="architect-send">
              Send →
            </button>
          </form>

          <p className="architect-footnote">Powered by Claude Sonnet · Anchored in the 81-book Ethiopian Christ Canon</p>
        </div>
      )}
    </>
  );
}
